import React from 'react'
// libs
import { useDispatch } from 'react-redux'
import { change } from 'redux-form'
// components
import { ReduxInput, ReduxSelect } from 'components/FormElements/ReduxForm'
import RecordPicker from './RecordPicker'
import { useInjuryMasterRecordNames } from './useInjuryMasterRecordNames'
// constants
import { Forms } from 'constants/forms'
// types
import { IOption } from 'components/FormElements/Select'
import { IInjuryMasterIncidentProperty, InjuryMasterPropertyKind } from 'types/graphql'
// utils
import { required } from 'utils/validators'

interface Props {
  name: string
  property: IInjuryMasterIncidentProperty
  // An option once chosen; the bare saved string (or, for a record list, a JSON array of URLs) until
  // the value has been turned into an option/options.
  value?: IOption | IOption[] | string
}

const YES_NO_OPTIONS: IOption[] = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' }
]

// A value set on the mapping itself instead of read from a Skefto field: a choice, yes/no, number,
// text, or one of Injury Master's own records. What is saved is a string — the choice code,
// "true"/"false", the number or text, an Injury Master record URL, or (for a record list) a JSON
// array of URLs.
const FixedValue: React.FC<Props> = ({ name, property, value }) => {
  const dispatch = useDispatch()

  const options: IOption[] | undefined = React.useMemo(() => {
    if (property.kind === InjuryMasterPropertyKind.Choice) {
      return (property.choices ?? []).map(({ value, label }) => ({ value: String(value), label }))
    }

    return property.kind === InjuryMasterPropertyKind.Boolean ? YES_NO_OPTIONS : undefined
  }, [property])

  // What was actually saved, captured once — after the effect below turns it into an option (or
  // options), `value` no longer holds the raw URL(s), so re-reading it would lose them.
  const initialValueRef = React.useRef(value)

  const recordUrls: string[] = React.useMemo(() => {
    const initial = initialValueRef.current

    if (property.kind === InjuryMasterPropertyKind.Record) {
      return typeof initial === 'string' && initial ? [initial] : []
    }

    if (property.kind === InjuryMasterPropertyKind.RecordList && typeof initial === 'string') {
      try {
        return JSON.parse(initial)
      } catch {
        return []
      }
    }

    return []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property.kind])

  const recordNames = useInjuryMasterRecordNames(recordUrls)

  // Saved mappings only hold the code (or, for a record, the URL) — show the real name once it is
  // known: the catalog for a choice/yes-no, or `recordNames` (a request of its own) for a record.
  //
  // Every branch dispatches only when the field doesn't already hold that exact value. Without that
  // check this loops forever: dispatching always creates a new `value` object even when its content
  // is unchanged, `value` is a dependency below, so the effect would re-run and dispatch again,
  // producing another new object, on and on.
  React.useEffect(() => {
    if (property.kind === InjuryMasterPropertyKind.Record) {
      const [url] = recordUrls
      const label = url ? (recordNames[url] ?? url) : undefined
      const current = !Array.isArray(value) && typeof value !== 'string' ? value : undefined

      if (url && (current?.value !== url || current?.label !== label)) {
        dispatch(change(Forms.SYSTEM_PROCESS_CREATE_FORM, `${name}.fixedValue`, { value: url, label }))
      }

      return
    }

    if (property.kind === InjuryMasterPropertyKind.RecordList) {
      const next = recordUrls.map((url) => ({ value: url, label: recordNames[url] ?? url }))
      const current = Array.isArray(value) ? value : []
      const isSame =
        current.length === next.length &&
        next.every((option, index) => current[index]?.value === option.value && current[index]?.label === option.label)

      if (recordUrls.length && !isSame) {
        dispatch(change(Forms.SYSTEM_PROCESS_CREATE_FORM, `${name}.fixedValue`, next))
      }

      return
    }

    const code =
      typeof value === 'string'
        ? value
        : !Array.isArray(value) && value?.label === value?.value
          ? value?.value
          : undefined
    const match = code === undefined ? undefined : options?.find((option) => option.value === code)

    if (match) {
      dispatch(change(Forms.SYSTEM_PROCESS_CREATE_FORM, `${name}.fixedValue`, match))
    }
  }, [dispatch, name, options, property, recordNames, recordUrls, value])

  if (property.kind === InjuryMasterPropertyKind.Record && property.listType) {
    return <RecordPicker name={`${name}.fixedValue`} label='Fixed value' listType={property.listType} />
  }

  if (property.kind === InjuryMasterPropertyKind.RecordList && property.listType) {
    return <RecordPicker isMultiple name={`${name}.fixedValue`} label='Fixed values' listType={property.listType} />
  }

  if (options) {
    return (
      <ReduxSelect
        validate={required}
        menuPortalTarget={null}
        label='Fixed value'
        name={`${name}.fixedValue`}
        options={options}
      />
    )
  }

  return (
    <ReduxInput
      validate={required}
      label='Fixed value'
      name={`${name}.fixedValue`}
      type={property.kind === InjuryMasterPropertyKind.Number ? 'number' : 'text'}
    />
  )
}

export default FixedValue
